import { isMixed, type Mixed } from './mixed';
import { NumberInputFormatter, type NumberInputFormatterOptions } from './numberInputFormatter';

/**
 * A field shown as a percentage over a value stored as a fraction of one, modelled on the editor's
 * `PercentageFormatter`.
 *
 * Bounded to a fraction of one, as opacity is. A quantity that may exceed it — letter spacing, line
 * height — has to pass its own bounds, since an explicit `undefined` still takes the default.
 */
export class PercentFormatter extends NumberInputFormatter {
  protected override allowedUnits = '%';

  constructor({ decimals = 0, min = 0, max = 1, ...opts }: NumberInputFormatterOptions = {}) {
    super({ decimals, min, max, ...opts });
  }

  override get displayUnit(): string {
    return '%';
  }

  protected override formatUnmixed(value: number): string {
    return `${super.formatUnmixed(value * 100)}%`;
  }

  override parse(value: string, currentValue?: number | Mixed): number {
    // The stored value is a fraction of one while the entry is written as 0 - 100. Both have to be
    // on the same scale for a relative entry to resolve against the current value.
    const scaled = currentValue === undefined || isMixed(currentValue) ? currentValue : currentValue * 100;

    return super.parse(value, scaled) / 100;
  }

  /** Nudging steps by a displayed percentage point, which is a hundredth of the stored value. */
  override getNudgeAmount(big: boolean): number {
    return big ? 0.1 : 0.01;
  }

  override toDisplay(stored: number): number {
    return stored * 100;
  }

  override fromDisplay(display: number): number {
    return display / 100;
  }
}
